import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import axios from 'axios'
import { Button, ButtonLabel, Header, Icon } from '@components/Generic'
import {
  GocardlessLoadErrorScreen,
  GocardlessLoadingScreen,
  GocardlessRedirectingScreen,
} from '@components/Gocardless/GocardlessScreens'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@methods/axiosConfig'
import { forceRedirect } from '@methods/forceRedirect'
import { devLog } from '@methods/devLog'
import { API_URL, GOCARDLESS_SERVICE_PATH } from '@constants/apiUrl'
import { cx } from '@/utils/cx'
import type { ConfirmIncomeRequest, IncomeConfirmation } from '@/types/gocardlessIncomeConfirmation'
import { IncomeCandidateCard, formatAmount } from './IncomeCandidateCard'

// `/gocardless-income-confirmation`: the rules behind the account transactions report could not
// settle the user's income and the flow asked to have the user point it out. The page is reached
// with the `gocardless-session` cookie set, either from the return page (POST complete answered
// `incomeConfirmationRequired`) or straight from the backend's redirecting GET complete. Like the
// return page it can land in a browser that never held the onboarding session cookie, so its
// scenario skips the basic context and the page is excluded from the keep-alive heartbeat.
//
// The transaction waits in `awaitingIncomeConfirmation` until `confirm-income` answers; only then
// is the session handed back to onboarding through `redirect-back` - the same way a completed
// session leaves the return page.

type Status = 'loading' | 'loadError' | 'select' | 'redirecting'
type Answer = 'confirm' | 'none'
// `final`: the backend refused the answer for good (400) - a retry cannot succeed
type SubmitError = 'retryable' | 'final' | null

const INCOME_CANDIDATES_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/income-candidates`
const CONFIRM_INCOME_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/confirm-income`
const REDIRECT_BACK_URL = `${API_URL}/${GOCARDLESS_SERVICE_PATH}/redirect-back`

const getIncomeConfirmation = async (): Promise<IncomeConfirmation> => {
  const { data } = await axiosInstance.get<IncomeConfirmation>(INCOME_CANDIDATES_URL, {
    withCredentials: true,
  })

  return data
}

const hasStatus = (error: unknown, status: number): boolean =>
  axios.isAxiosError(error) && error.response?.status === status

export const GocardlessIncomeConfirmationPage = observer(() => {
  const store = useContext(StoreContext)
  const { gocardlessIncomeConfirmationPage: trans } = store.TranslationsState.translations
  const { language } = store.TranslationsState

  const [status, setStatus] = useState<Status>('loading')
  const [confirmation, setConfirmation] = useState<IncomeConfirmation | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [pending, setPending] = useState<Answer | null>(null)
  const [submitError, setSubmitError] = useState<SubmitError>(null)

  const redirectBack = () => {
    setStatus('redirecting')
    forceRedirect(REDIRECT_BACK_URL)
  }

  const load = async () => {
    setStatus('loading')

    try {
      const data = await getIncomeConfirmation()

      // Already answered (a reload after confirming) or never required: there is nothing to ask,
      // the session only has to be handed back to onboarding.
      if (data.answer || !data.required) {
        redirectBack()
        return
      }

      setConfirmation(data)
      setStatus('select')
    } catch (e) {
      devLog('Error fetching income candidates:', e)

      // This session was never asked (a flow without the feature): nothing to do here either.
      if (hasStatus(e, 404)) {
        redirectBack()
        return
      }

      setStatus('loadError')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const toggleCandidate = (id: string) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]))
  }

  const answer = async (kind: Answer) => {
    if (pending) return

    setPending(kind)
    setSubmitError(null)

    const body: ConfirmIncomeRequest = {
      confirmedCandidateIds: kind === 'confirm' ? selectedIds : [],
    }

    try {
      await axiosInstance.post<void>(CONFIRM_INCOME_URL, body, { withCredentials: true })
      redirectBack()
    } catch (e) {
      devLog('Error confirming income:', e)

      if (hasStatus(e, 400)) {
        // Unknown id, a session no longer waiting for the answer, or a second answer. The last
        // one also happens when a retry follows a lost response - then the answer is stored and
        // the user can simply be sent on.
        const latest = await getIncomeConfirmation().catch(() => null)

        if (latest?.answer) {
          redirectBack()
          return
        }

        setSubmitError('final')
      } else {
        setSubmitError('retryable')
      }

      setPending(null)
    }
  }

  if (status === 'loading') {
    return <GocardlessLoadingScreen className="w-500 p-40" />
  }

  if (status === 'redirecting') {
    return (
      <GocardlessRedirectingScreen
        className="w-500 p-40"
        header={trans?.redirectingHeader}
        label={trans?.redirectingLabel}
        buttonLabel={trans?.pleaseWait}
      />
    )
  }

  if (status === 'loadError') {
    return (
      <GocardlessLoadErrorScreen
        className="w-500 p-40"
        header={trans?.header}
        message={trans?.loadError}
        retryLabel={trans?.tryAgain}
        retryTestId="income-confirmation-retry"
        onRetry={() => void load()}
      />
    )
  }

  const candidates = confirmation?.candidates ?? []
  const currency = confirmation?.currency ?? null
  const hasCandidates = candidates.length > 0
  // candidates share one currency, so their monthly amounts can simply be added up
  const selectedTotal = candidates
    .filter((candidate) => selectedIds.includes(candidate.id))
    .reduce((sum, candidate) => sum + candidate.typicalMonthly, 0)
  const locked = pending !== null || submitError === 'final'

  const renderButtons = () => {
    if (submitError === 'final') {
      return (
        <Button
          className="button--w164"
          onClick={redirectBack}
          dataTestid="income-confirmation-back"
        >
          {trans?.backToOnboarding}
        </Button>
      )
    }

    if (!hasCandidates) {
      return (
        <Button
          className="button--w164"
          onClick={() => void answer('none')}
          disabled={locked}
          dataTestid="income-confirmation-continue"
        >
          <ButtonLabel isLoading={pending === 'none'} label={trans?.continue} />
        </Button>
      )
    }

    return (
      <>
        <Button
          className="button--secondary"
          onClick={() => void answer('none')}
          disabled={locked}
          dataTestid="income-confirmation-none"
        >
          <ButtonLabel isLoading={pending === 'none'} label={trans?.noneOfThese} />
        </Button>

        <Button
          onClick={() => void answer('confirm')}
          disabled={locked || selectedIds.length === 0}
          dataTestid="income-confirmation-confirm"
        >
          <ButtonLabel isLoading={pending === 'confirm'} label={trans?.confirm} />
        </Button>
      </>
    )
  }

  return (
    <div className="page-container mobile-m-0 w-500 p-40">
      <Header className="no-margin">{trans?.header}</Header>

      <p className="generic__paragraph">
        {hasCandidates ? trans?.description : trans?.noCandidates}
      </p>

      {hasCandidates && (
        <div className="gocardless-ic__notice" data-testid="income-confirmation-privacy">
          <span className="gocardless-ic__notice-icon">
            <Icon type="info" size="20px" color="var(--gc-notice-icon-color, #072543)" />
          </span>
          <p className="gocardless-ic__notice-text">
            <span className="gocardless-ic__notice-title">{trans?.privacyNotice?.title}</span>{' '}
            {trans?.privacyNotice?.text}
          </p>
        </div>
      )}

      {hasCandidates && (
        <div className="gocardless-ic__candidates" role="group" aria-label={trans?.header}>
          {candidates.map((candidate) => (
            <IncomeCandidateCard
              key={candidate.id}
              candidate={candidate}
              currency={currency}
              selected={selectedIds.includes(candidate.id)}
              disabled={locked}
              onToggle={() => toggleCandidate(candidate.id)}
            />
          ))}
        </div>
      )}

      {hasCandidates && (
        <div
          className="gocardless-ic__total"
          aria-live="polite"
          data-testid="income-confirmation-total"
        >
          <span className="gocardless-ic__total-label">{trans?.selectedTotal}</span>
          <span className="gocardless-ic__amount">
            <span className="gocardless-ic__amount-value">
              {formatAmount(selectedTotal, currency, language)}
            </span>
            <span className="gocardless-ic__amount-label">{trans?.perMonth}</span>
          </span>
        </div>
      )}

      {submitError && (
        <p className="generic__paragraph generic__error-message" role="alert">
          {trans?.submitError}
        </p>
      )}

      <div
        className={cx(
          'layout__content-container-bottom-fixed',
          'layout__content-container-bottom-fixed--no-border',
          hasCandidates &&
            submitError !== 'final' &&
            'layout__content-container-bottom-fixed--stacked'
        )}
      >
        {renderButtons()}
      </div>
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </div>
  )
})
